/**
 * Course Setting Utilities
 *
 * Handles course plotting for voyage navigation, including:
 * - DC calculation based on destination and chart quality
 * - Failure distance calculations
 * - Course setting check resolution
 */

export type VoyageDistance = 'close' | 'distant';
export type DestinationFamiliarity = 'very familiar' | 'studied carefully' | 'seen once' | 'uncertain' | 'mythical or legendary';
export type StartPointKnowledge = 'well known' | 'uncertain' | 'only guessed at' | 'completely unknown';
export type ChartQuality = 'none' | 'excellent' | 'poor' | 'extremely poor' | 'false';

export interface CourseSettingParams {
  distance: VoyageDistance;
  familiarity: DestinationFamiliarity;
  startPointKnowledge: StartPointKnowledge;
  chartQuality: ChartQuality;
}

export interface CourseSettingResult {
  roll: number;
  totalBonus: number;
  total: number;
  dc: number;
  success: boolean;
  missedBy: number;
  missDistance?: string;
  description: string;
}

/**
 * Calculate base DC based on destination distance and familiarity
 */
export function getBaseDC(distance: VoyageDistance, familiarity: DestinationFamiliarity): number {
  const dcMap: Record<VoyageDistance, Record<DestinationFamiliarity, number>> = {
    close: {
      'very familiar': 5,
      'studied carefully': 10,
      'seen once': 20,
      'uncertain': 25,
      'mythical or legendary': 35
    },
    distant: {
      'very familiar': 10,
      'studied carefully': 15,
      'seen once': 25,
      'uncertain': 30,
      'mythical or legendary': 35
    }
  };

  return dcMap[distance][familiarity];
}

/**
 * Get DC modifier based on starting point knowledge
 */
export function getStartPointModifier(knowledge: StartPointKnowledge): number {
  const modifiers: Record<StartPointKnowledge, number> = {
    'well known': 0,
    'uncertain': 5,
    'only guessed at': 10,
    'completely unknown': 20
  };

  return modifiers[knowledge];
}

/**
 * Get DC modifier based on chart quality
 */
export function getChartModifier(quality: ChartQuality): number {
  const modifiers: Record<ChartQuality, number> = {
    'none': 0,
    'excellent': -2,
    'poor': 2,
    'extremely poor': 5,
    'false': 10
  };

  return modifiers[quality];
}

/**
 * Calculate the total DC for a course setting check
 */
export function calculateCourseSettingDC(params: CourseSettingParams): number {
  const baseDC = getBaseDC(params.distance, params.familiarity);
  const startPointMod = getStartPointModifier(params.startPointKnowledge);
  const chartMod = getChartModifier(params.chartQuality);

  return baseDC + startPointMod + chartMod;
}

/**
 * Roll dice for miss distance calculation
 * @param numDice Number of dice to roll
 * @param dieSize Size of the die (10 or 100)
 * @returns Array of individual rolls
 */
function rollDice(numDice: number, dieSize: number): number[] {
  const rolls: number[] = [];
  for (let i = 0; i < numDice; i++) {
    rolls.push(Math.floor(Math.random() * dieSize) + 1);
  }
  return rolls;
}

/**
 * Calculate miss distance based on failure margin
 */
export function calculateMissDistance(
  distance: VoyageDistance,
  missedBy: number,
  autoRoll: boolean = true
): { distance: string; rolls: number[] | undefined } {
  if (missedBy <= 0) {
    return { distance: 'On target', rolls: undefined };
  }

  let numDice: number;
  let dieSize: number;

  if (missedBy <= 5) {
    // Failed by 5 or less
    numDice = 5;
    dieSize = distance === 'close' ? 10 : 100;
  } else {
    // Failed by more than 5
    numDice = 10;
    dieSize = distance === 'close' ? 10 : 100;
  }

  if (autoRoll) {
    const rolls = rollDice(numDice, dieSize);
    const total = rolls.reduce((sum, roll) => sum + roll, 0);
    const unit = dieSize === 10 ? 'miles' : 'miles';
    return {
      distance: `${total} ${unit} off course`,
      rolls
    };
  } else {
    return {
      distance: `Roll ${numDice}d${dieSize} miles`,
      rolls: undefined
    };
  }
}

/**
 * Process a course setting check
 */
export function processCourseSettingCheck(
  params: CourseSettingParams,
  d20Roll: number,
  skillBonus: number,
  autoRollMiss: boolean = true
): CourseSettingResult {
  const dc = calculateCourseSettingDC(params);
  const total = d20Roll + skillBonus;
  const success = total >= dc;
  const missedBy = success ? 0 : dc - total;

  let missDistanceInfo = { distance: '', rolls: undefined as number[] | undefined };
  if (!success) {
    missDistanceInfo = calculateMissDistance(params.distance, missedBy, autoRollMiss);
  }

  // Generate description
  let description = '';
  if (success) {
    description = `Success! The pilot successfully plots a course to the destination.`;
  } else if (missedBy <= 5) {
    description = `The pilot arrives in the vicinity of the destination, missing by ${missDistanceInfo.distance}.`;
  } else {
    description = `The pilot is wildly off course, missing the destination by ${missDistanceInfo.distance}.`;
  }

  return {
    roll: d20Roll,
    totalBonus: skillBonus,
    total,
    dc,
    success,
    missedBy,
    missDistance: missDistanceInfo.distance,
    description
  };
}

/**
 * Format course setting parameters for display
 */
export function formatCourseSettingParams(params: CourseSettingParams): string {
  const baseDC = getBaseDC(params.distance, params.familiarity);
  const startMod = getStartPointModifier(params.startPointKnowledge);
  const chartMod = getChartModifier(params.chartQuality);
  const totalDC = calculateCourseSettingDC(params);

  let breakdown = `Base DC: ${baseDC} (${params.distance}, ${params.familiarity})`;

  if (startMod !== 0) {
    breakdown += `\nStart Point: ${startMod > 0 ? '+' : ''}${startMod} (${params.startPointKnowledge})`;
  }

  if (chartMod !== 0 && params.chartQuality !== 'none') {
    breakdown += `\nChart Quality: ${chartMod > 0 ? '+' : ''}${chartMod} (${params.chartQuality})`;
  }

  breakdown += `\n\nTotal DC: ${totalDC}`;

  return breakdown;
}
